import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { ErrorMessage } from "@hookform/error-message";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import useMakeRequest from "../../utils/ApiHelper";
import { ApiConstants } from "../../constants/ApiConstants";
import ChangePasswordValidation from "../../Validation/ChangePasswordValidation";
import BrandedLoaderOverlay from "../layout/BrandedLoaderOverlay";
import { LanguageConstants } from "../../constants/LanguageConstants";
import { Routes as AppRoutes } from "../../constants/RouteConstants";
import mainLogoShort from "../../Assets/images/cms-logo.png";

interface FormInputs {
  old_password: string;
  password: string;
  confirm_password: string;
}

function ChangePasswordPage() {
  const navigate = useNavigate();
  const profiledata = useSelector((state: any) => state?.ProfileState);
  const Changepassword = useMakeRequest();
  const [old, setOld] = useState(false);
  const [newp, setNewp] = useState(false);
  const [cpassword, setCpassword] = useState(false);

  const {
    register,
    setValue,
    formState: { errors },
    handleSubmit,
  } = useForm<FormInputs>({
    mode: "onChange",
    resolver: yupResolver(ChangePasswordValidation),
  });

  useEffect(() => {
    if (profiledata?.data?.data?.social_id) {
      navigate(AppRoutes.Profile, { replace: true });
    }
  }, [navigate, profiledata?.data?.data?.social_id]);

  useEffect(() => {
    if (!Changepassword?.status) return;
    setValue("confirm_password", "");
    setValue("old_password", "");
    setValue("password", "");
    setOld(false);
    setNewp(false);
    setCpassword(false);
    Changepassword.reset();
    navigate(AppRoutes.Profile);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset is stable; avoid re-running on Changepassword identity
  }, [Changepassword?.status, navigate, setValue]);

  const onSubmit = (payload: FormInputs) => {
    Changepassword.makeRequest({ payload, url: ApiConstants?.ChangePassword, toastStatus: true });
  };

  return (
    <>
      <BrandedLoaderOverlay active={Boolean(Changepassword?.loading)} />
      <main className="change-password-page head-space">
        <div className="inner-content">
          <div className="container">
            <div className="page-box">
              <div className="page-box-inner pb-4">
                <div className="change-password-card change-password-card--page">
                  <h1 className="sec-ttl text-capitalize mb-4 mb-md-0">{LanguageConstants?.CHANGEPASSWORD}</h1>

                  <div className="row">
                    <div className="col-sm-9 col-md-6 col-lg-5 col-xxl-4 mx-auto">
                      <div className="change-password-card__brand">
                        <img src={mainLogoShort} alt="" className="change-password-card__logo" />
                      </div>
                      <p className="change-password-card__subtitle text-black mt-3">
                        {LanguageConstants?.CHANGE_PASSWORD_SUBTITLE ?? "Set a new password for your account"}
                      </p>
                      <form className="change-password-card__form" onSubmit={handleSubmit(onSubmit)} noValidate>
                        <div className="mb-4">
                          <div className="change-password-card__field">
                            <i className="icon-unlock change-password-card__fieldIcon" aria-hidden />
                            <input
                              className="change-password-card__input"
                              type={old ? "text" : "password"}
                              placeholder={LanguageConstants?.OLD_PASSOWRD}
                              autoComplete="current-password"
                              {...register("old_password")}
                            />
                            <button
                              type="button"
                              className="change-password-card__eye"
                              onClick={() => setOld(!old)}
                              aria-label={old ? "Hide password" : "Show password"}
                            >
                              <i className={old ? "icon-eye-open" : "icon-eye-slash-fill"} aria-hidden />
                            </button>
                          </div>
                          <ErrorMessage
                            errors={errors}
                            name="old_password"
                            render={({ message }) => <p className="change-password-card__error mt-1">{message}</p>}
                          />
                        </div>
                        <div className="mb-4">
                          <div className="change-password-card__field">
                            <i className="icon-lock-2 change-password-card__fieldIcon" aria-hidden />
                            <input
                              className="change-password-card__input"
                              type={newp ? "text" : "password"}
                              placeholder={LanguageConstants?.NEW_PASSWORD}
                              autoComplete="new-password"
                              {...register("password")}
                            />
                            <button
                              type="button"
                              className="change-password-card__eye"
                              onClick={() => setNewp(!newp)}
                              aria-label={newp ? "Hide password" : "Show password"}
                            >
                              <i className={newp ? "icon-eye-open" : "icon-eye-slash-fill"} aria-hidden />
                            </button>
                          </div>
                          <ErrorMessage
                            errors={errors}
                            name="password"
                            render={({ message }) => <p className="change-password-card__error mt-1">{message}</p>}
                          />
                        </div>
                        <div className="mb-4">
                          <div className="change-password-card__field">
                            <i className="icon-lock-2 change-password-card__fieldIcon" aria-hidden />
                            <input
                              className="change-password-card__input"
                              type={cpassword ? "text" : "password"}
                              placeholder={LanguageConstants?.CONFIRMA_PASSWORD}
                              autoComplete="new-password"
                              {...register("confirm_password")}
                            />
                            <button
                              type="button"
                              className="change-password-card__eye"
                              onClick={() => setCpassword(!cpassword)}
                              aria-label={cpassword ? "Hide password" : "Show password"}
                            >
                              <i className={cpassword ? "icon-eye-open" : "icon-eye-slash-fill"} aria-hidden />
                            </button>
                          </div>
                          <ErrorMessage
                            errors={errors}
                            name="confirm_password"
                            render={({ message }) => <p className="change-password-card__error mt-1">{message}</p>}
                          />
                        </div>
                        <button type="submit" className="round-btn w-75 mx-auto mt-5" disabled={Boolean(Changepassword?.loading)}>
                          {LanguageConstants?.UPDATE ?? "Update"}
                        </button>
                      </form>

                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}

export default ChangePasswordPage;
